import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Input, Button, Card, Typography, Space, Row, Col, Select, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

function RecipeGenerator() {
  const [ingredients, setIngredients] = useState("");
  const [cuisine, setCuisine] = useState("any");
  const [dietaryRestrictions, setDietaryRestrictions] = useState("");
  const [recipe, setRecipe] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { logout } = useAuth();

  const createRecipe = async () => {
    if (!ingredients.trim()) {
      setError("Please enter ingredients before generating a recipe");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem("token");

      if (!token) {
        setError("Session expired. Please log in again.");
        logout();
        return;
      }

      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${apiUrl}/api/recipe-creator?ingredients=${encodeURIComponent(ingredients)}&cuisine=${encodeURIComponent(cuisine)}&dietaryRestrictions=${encodeURIComponent(dietaryRestrictions)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401 || response.status === 403) {
        setError("Your session has expired. Please log in again.");
        logout();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.text();
      setRecipe(data);
      message.success("Recipe generated successfully!");
    } catch (error) {
      console.error("Error generating recipe:", error);
      setError(error.message || "An error occurred while generating the recipe");
    } finally {
      setLoading(false);
    }
  };

  const clearRecipe = () => {
    setIngredients("");
    setCuisine("any");
    setDietaryRestrictions("");
    setRecipe("");
    setError(null);
  };

  const sampleIngredients = [
    "chicken, rice, vegetables",
    "pasta, tomatoes, basil",
    "beef, potatoes, onions",
    "eggs, flour, sugar",
    "fish, lemon, herbs"
  ];

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: '24px', color: '#1890ff' }}>
        🍳 Create a Recipe
      </Title>
      
      {error && (
        <ErrorMessage 
          message="Error" 
          description={error} 
          type="error" 
          closable={true}
        />
      )}

      <Card title="Recipe Generator" style={{ marginBottom: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>Main Ingredients</Text>
            <TextArea
              value={ingredients}
              onChange={(e) => setIngredients(e.target.value)}
              placeholder="Enter main ingredients (e.g., chicken, rice, vegetables)"
              rows={3}
              style={{ borderRadius: '8px' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text style={{ fontSize: '12px', color: '#666' }}>Try: </Text>
              {sampleIngredients.map((sample, index) => (
                <Button
                  key={index}
                  size="small"
                  type="link"
                  onClick={() => setIngredients(sample)}
                  style={{ padding: '0 4px', fontSize: '12px' }}
                >
                  {sample}
                </Button>
              ))}
            </div>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Cuisine Type</Text>
              <Select
                value={cuisine}
                onChange={setCuisine}
                style={{ width: '100%' }}
                placeholder="Select cuisine type"
              >
                <Option value="any">Any</Option>
                <Option value="Italian">Italian</Option>
                <Option value="Chinese">Chinese</Option>
                <Option value="Indian">Indian</Option>
                <Option value="Mexican">Mexican</Option>
                <Option value="French">French</Option>
                <Option value="Thai">Thai</Option>
                <Option value="Mediterranean">Mediterranean</Option>
                <Option value="Japanese">Japanese</Option>
              </Select>
            </Col>
            <Col span={12}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Dietary Restrictions</Text>
              <Input
                value={dietaryRestrictions}
                onChange={(e) => setDietaryRestrictions(e.target.value)}
                placeholder="e.g., gluten-free, vegan, nut-free"
                style={{ borderRadius: '8px' }}
              />
            </Col>
          </Row>

          <Row gutter={16} justify="space-between">
            <Col>
              <Button onClick={clearRecipe} style={{ marginRight: '8px' }}>
                Clear
              </Button>
            </Col>
            <Col>
              <Button 
                type="primary" 
                onClick={createRecipe}
                loading={loading}
                disabled={loading || !ingredients.trim()}
                style={{ 
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff'
                }}
              >
                {loading ? 'Creating...' : 'Create Recipe'}
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      {loading && <LoadingSpinner tip="Creating your recipe..." />}

      {recipe && !loading && (
        <Card title="Generated Recipe" style={{ backgroundColor: '#fafafa', border: '1px solid #f0f0f0' }}>
          <div style={{ whiteSpace: 'pre-line', fontSize: '16px', lineHeight: '1.8' }}>
            {recipe}
          </div>
        </Card>
      )}

      {!recipe && !loading && (
        <Card style={{ textAlign: 'center', color: '#8c8c8c' }}>
          <Paragraph>
            Enter your ingredients and preferences above to generate a personalized recipe.
            <br />
            The AI will create a delicious recipe based on what you have available!
          </Paragraph>
        </Card>
      )}
    </div>
  );
}

export default RecipeGenerator;
